import React, { useState, useEffect } from 'react';
import { Backpack, Settings } from 'lucide-react';
import { CharacterData } from '../types/character';
import { loadClassById } from '../utils/classDataLoader';
import { loadHeritageData } from '../utils/heritageDataLoader';
import { loadCommunityData } from '../utils/communityDataLoader';
import Navigation from '../components/Navigation/Navigation';
import IdentitySection from '../components/Biography/IdentitySection';
import AppearanceSection from '../components/Biography/AppearanceSection';
import BackgroundSection from '../components/Biography/BackgroundSection';
import ConnectionsSection from '../components/Biography/ConnectionsSection';
import JournalSection from '../components/Biography/JournalSection';
import BiographySection from '../components/Biography/BiographySection';
import BiographyCard from '../components/Biography/BiographyCard';
import './BiographyPage.css';

interface BiographyPageProps {
  character: CharacterData | null;
  updateCharacterData: (updates: any) => void;
  disableAutoSave?: () => void;
  enableAutoSave?: () => void;
}

interface ClassBiographyData {
  descriptions?: {
    clothes?: string[];
    eyes?: string[];
    body?: string[];
    skin?: string[];
    attitude?: string[];
  };
  backgroundQuestions?: string[];
  connections?: string[];
}

const BiographyPage: React.FC<BiographyPageProps> = ({ character, updateCharacterData, disableAutoSave, enableAutoSave }) => {
  const [classBiographyData, setClassBiographyData] = useState<ClassBiographyData | null>(null);
  const [heritageData, setHeritageData] = useState<any>(null);
  const [communityData, setCommunityData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBiographyData = async () => {
      try {
        setIsLoading(true);
        
        // Load class data for biography information
        if (character?.class) {
          const classData = await loadClassById(character.class);
          if (classData?.characterCreation) {
            setClassBiographyData(classData.characterCreation);
          }
        }
        
        // Load heritage data
        if (character?.heritage) {
          const heritage = await loadHeritageData(character.heritage);
          setHeritageData(heritage);
        }
        
        // Load community data
        if (character?.community) {
          const community = await loadCommunityData(character.community);
          setCommunityData(community);
        }
      } catch (error) {
        console.error('Error loading biography data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBiographyData();
  }, [character?.class, character?.heritage, character?.community]);

  if (!character) {
    return (
      <div className="biography-page">
        <Navigation />
        <div className="biography-page__content">
          <div className="biography-page__loading">
            <h1>Character Biography</h1>
            <p>Character data is loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const biography = character.biography;

  if (isLoading) {
    return (
      <div className="biography-page">
        <Navigation />
        <div className="biography-page__content">
          <div className="biography-page__loading">
            <h1>{character.name}'s Biography</h1>
            <p>Loading character details...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="biography-page">
      <Navigation />
      <div className="biography-page__content">
        {/* Biography Sections using new composable system */}
        <div className="biography-page__sections">

          {/* Character Identity */}
          <IdentitySection
            character={character}
            heritageData={heritageData}
            communityData={communityData}
            defaultExpanded={true}
            priority="high"
          />

          {/* Character Appearance */}
          <AppearanceSection
            character={character}
            updateCharacterData={updateCharacterData}
            defaultExpanded={true}
            priority="high"
          />

          {/* Background Answers */}
          <BackgroundSection
            character={character}
            updateCharacterData={updateCharacterData}
            defaultExpanded={true}
            priority="high"
          />

          {/* Party Connections */}
          <ConnectionsSection
            character={character}
            updateCharacterData={updateCharacterData}
            defaultExpanded={true}
            priority="medium"
          />

          {/* Character Journal */}
          <JournalSection
            character={character}
            updateCharacterData={updateCharacterData}
            defaultExpanded={false}
            priority="medium"
          />

          {/* Starting Choices */}
          <BiographySection
            sectionId="bio-choices"
            title="Personal Items & Starting Choices"
            icon={<Backpack size={18} />}
            defaultExpanded={false}
            priority="medium"
            isEmpty={!character.biography?.startingChoices || character.biography.startingChoices.length === 0}
            emptyMessage="No starting choices recorded during character creation"
          >
            {character.biography?.startingChoices && character.biography.startingChoices.length > 0 && (
              <BiographyCard variant="default" padding="md">
                <div className="choices-grid">
                  {character.biography.startingChoices.map((choice, index) => (
                    <div key={index} className="choice-item">
                      <span className="choice-category">{choice.category}</span>
                      <span className="choice-value">{choice.choice}</span>
                      {choice.description && (
                        <p className="choice-description">{choice.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </BiographyCard>
            )}
          </BiographySection>

        </div>
      </div>
    </div>
  );
};

export default BiographyPage;